// manifest 字段 / 文件清单编辑器（新建类型草稿与管理员编辑共用）
import type { ManifestField, ManifestFileReq, ManifestSchema, RequiredWhen } from "@csp/shared";

const TYPES: { v: ManifestField["type"]; t: string }[] = [
  { v: "string", t: "文本" },
  { v: "text", t: "多行文本" },
  { v: "number", t: "数字" },
  { v: "date", t: "日期" },
  { v: "enum", t: "下拉选择" },
  { v: "boolean", t: "是/否" },
];

export function SchemaEditor({ value, onChange }: { value: ManifestSchema; onChange: (v: ManifestSchema) => void }) {
  const setField = (i: number, patch: Partial<ManifestField>) =>
    onChange({ ...value, fields: value.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const setFile = (i: number, patch: Partial<ManifestFileReq>) =>
    onChange({ ...value, files: value.files.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const move = <T,>(arr: T[], i: number, d: number): T[] => {
    const a = [...arr];
    const j = i + d;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j] as T, a[i] as T];
    return a;
  };
  return (
    <div>
      <h3>字段</h3>
      <div className="editor-row small muted">
        <span>key（英文）</span>
        <span>显示名</span>
        <span>类型</span>
        <span>必填</span>
        <span>格式（正则）/ 选项 / 提示</span>
        <span />
      </div>
      {value.fields.map((f, i) => (
        <div className="editor-row" key={i} data-testid="schema-field">
          <input value={f.key} placeholder="postalCode" aria-label="字段 key" onChange={(e) => setField(i, { key: e.target.value })} />
          <input value={f.label} placeholder="邮编" aria-label="字段显示名" onChange={(e) => setField(i, { label: e.target.value })} />
          <select value={f.type} aria-label="字段类型" onChange={(e) => setField(i, { type: e.target.value as ManifestField["type"] })}>
            {TYPES.map((t) => (
              <option key={t.v} value={t.v}>{t.t}</option>
            ))}
          </select>
          <input type="checkbox" checked={f.required} aria-label="必填" onChange={(e) => setField(i, { required: e.target.checked })} style={{ width: 20 }} />
          <div className="stack">
            {f.type === "enum" ? (
              <input
                value={(f.options ?? []).join(",")}
                placeholder="选项，用逗号分隔"
                aria-label="选项"
                onChange={(e) => setField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              />
            ) : (
              <input value={f.pattern ?? ""} placeholder="正则（可空），如 ^\d{6}$" aria-label="格式" onChange={(e) => setField(i, { pattern: e.target.value || undefined })} />
            )}
            <input value={f.hint ?? ""} placeholder="提示（可空）" aria-label="提示" onChange={(e) => setField(i, { hint: e.target.value || undefined })} />
          </div>
          <div className="row">
            <button type="button" className="btn btn-small" onClick={() => onChange({ ...value, fields: move(value.fields, i, -1) })} title="上移">↑</button>
            <button type="button" className="btn btn-small btn-danger" onClick={() => onChange({ ...value, fields: value.fields.filter((_, j) => j !== i) })}>删</button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-small"
        onClick={() => onChange({ ...value, fields: [...value.fields, { key: "", label: "", type: "string", required: false }] })}
      >
        ＋ 加字段
      </button>
      <h3 style={{ marginTop: 18 }}>必备文件</h3>
      {value.files.map((f, i) => (
        <div className="row" key={i} style={{ marginBottom: 8 }} data-testid="schema-file">
          <input style={{ width: 180 }} value={f.key} placeholder="board_resolution" aria-label="文件 key" onChange={(e) => setFile(i, { key: e.target.value })} />
          <input style={{ width: 240 }} value={f.label} placeholder="董事会决议" aria-label="文件显示名" onChange={(e) => setFile(i, { label: e.target.value })} />
          <input
            style={{ width: 180 }}
            value={(f.accept ?? []).join(",")}
            placeholder="pdf,jpg（可空）"
            aria-label="允许格式"
            onChange={(e) => setFile(i, { accept: e.target.value.split(",").map((s) => s.trim().replace(/^\./, "")).filter(Boolean) })}
          />
          <label className="check">
            <input type="checkbox" checked={f.required} onChange={(e) => setFile(i, { required: e.target.checked, ...(e.target.checked ? { requiredWhen: undefined } : {}) })} />
            必须
          </label>
          {!f.required && <CondEditor fields={value.fields} value={f.requiredWhen} onChange={(w) => setFile(i, { requiredWhen: w })} />}
          <label className="check">
            <input type="checkbox" checked={!!f.multiple} onChange={(e) => setFile(i, { multiple: e.target.checked })} />
            可多份
          </label>
          <button type="button" className="btn btn-small btn-danger" onClick={() => onChange({ ...value, files: value.files.filter((_, j) => j !== i) })}>删</button>
        </div>
      ))}
      <button type="button" className="btn btn-small" onClick={() => onChange({ ...value, files: [...value.files, { key: "", label: "", required: true }] })}>
        ＋ 加文件
      </button>
    </div>
  );
}

/** 提交前清理：去掉空行、空 options；其它属性（来源文件、识别方法、条件必填、分组、指南等）原样保留，别在保存时丢掉 */
export function cleanSchema(s: ManifestSchema): ManifestSchema {
  return {
    version: s.version,
    fields: s.fields
      .filter((f) => f.key.trim() && f.label.trim())
      .map(({ options, pattern, hint, ...rest }) => ({
        ...rest,
        key: rest.key.trim(),
        label: rest.label.trim(),
        ...(rest.type === "enum" ? { options: options ?? [] } : {}),
        ...(pattern && rest.type !== "enum" ? { pattern } : {}),
        ...(hint ? { hint } : {}),
      })),
    files: s.files
      .filter((f) => f.key.trim() && f.label.trim())
      .map(({ multiple, accept, ...rest }) => ({
        ...rest,
        key: rest.key.trim(),
        label: rest.label.trim(),
        ...(multiple ? { multiple: true } : {}),
        ...(accept?.length ? { accept } : {}),
      })),
  };
}

/** 条件必填：当某个是否 / 下拉字段取某个值时才必须 */
function CondEditor({ fields, value, onChange }: { fields: ManifestField[]; value?: RequiredWhen; onChange: (w: RequiredWhen | undefined) => void }) {
  const opts = fields.filter((x) => x.type === "boolean" || x.type === "enum");
  const cur = opts.find((x) => x.key === value?.field);
  return (
    <span className="row" style={{ gap: 4 }}>
      <select aria-label="条件必须" value={value?.field ?? ""} style={{ width: "auto" }} onChange={(e) => {
        const g = opts.find((x) => x.key === e.target.value);
        onChange(g ? { field: g.key, equals: g.type === "boolean" ? true : g.options?.[0] ?? "" } : undefined);
      }}>
        <option value="">无条件</option>
        {opts.map((x) => <option key={x.key} value={x.key}>当「{x.label}」</option>)}
      </select>
      {cur && (cur.type === "boolean"
        ? <select aria-label="条件取值" value={value!.equals === true ? "1" : "0"} style={{ width: "auto" }} onChange={(e) => onChange({ field: cur.key, equals: e.target.value === "1" })}><option value="1">为 是 时必须</option><option value="0">为 否 时必须</option></select>
        : <select aria-label="条件取值" value={String(value!.equals)} style={{ width: "auto" }} onChange={(e) => onChange({ field: cur.key, equals: e.target.value })}>{(cur.options ?? []).map((o) => <option key={o} value={o}>为「{o}」时必须</option>)}</select>)}
    </span>
  );
}
